import { MAX_COURSE } from '@shared/const';

const SUPPORTED_LOCALES = ['no', 'en'];

export const isSupportedLocale = (langCode) => {
  return SUPPORTED_LOCALES.includes(langCode);
};

export const parseLanguageFromFilename = (filename) => {
  const upperName = filename.toUpperCase();
  if (
    upperName.startsWith('NO_') ||
    upperName.includes('_NO_') ||
    upperName.includes('_NO.')
  ) {
    return 'no';
  }
  if (
    upperName.startsWith('EN_') ||
    upperName.includes('_EN_') ||
    upperName.includes('_EN.')
  ) {
    return 'en';
  }
  return null;
};

export const validateSubtitles = (subtitles) => {
  const hasUnknownLang = subtitles.some(
    (s) => !isSupportedLocale(s.language.code)
  );

  const validLangs = subtitles
    .filter((s) => isSupportedLocale(s.language.code))
    .map((s) => s.language.code);
  const uniqueLangs = new Set(validLangs);
  const hasDuplicates = validLangs.length !== uniqueLangs.size;

  return { hasUnknownLang, hasDuplicates };
};

export const validateCourseFiles = (
  course,
  files,
  isSubtitles,
  numEpisodes
) => {
  const regex = /^([0-9]+)_(?:(.+)\.mp4|([a-z]{2}_)?(.+)\.(srt|vtt))$/i;

  const existingEpisodes = course ? course.episodes : [];
  const episodes = {};
  const validatedFiles = {};
  const subtitleData = {};
  const ignored = [];
  const errors = [];

  for (let i = 0; i < Object.keys(files).length; i += 1) {
    const uuid = Object.keys(files)[i];
    const file = { ...files[uuid], error: null };

    if (!file.aborted) {
      const fileName = file.name;
      const matches = fileName.match(regex);

      if (matches) {
        const prefix = parseInt(matches[1], 10);

        if (Number.isNaN(prefix) || prefix < 0) {
          ignored.push(file);
        } else if (matches[2]) {
          if (!episodes[prefix]) {
            if (!isSubtitles) {
              if (matches[2].length > MAX_COURSE.NAME_LENGTH) {
                file.error = `Episode name cannot exceed ${MAX_COURSE.NAME_LENGTH} characters.`;
                errors.push(file.error);
              } else {
                episodes[prefix] = { file, name: matches[2] };
              }
            } else {
              file.error = `Only subtitles allowed`;
              errors.push(file.error);
            }
          } else {
            file.error = `Repeated episode number: ${prefix}`;
            errors.push(file.error);
          }
        } else {
          const subtitleLocale = matches[3]?.slice(0, 2).toLowerCase();
          if (!subtitleData[prefix]) {
            subtitleData[prefix] = {};
          }
          if (subtitleData[prefix][subtitleLocale || 'unknown']) {
            file.error = `Repeated subtitle for episode ${prefix} with locale ${subtitleLocale || 'unknown'}`;
            errors.push(file.error);
          } else {
            subtitleData[prefix][subtitleLocale || 'unknown'] = {
              file,
              locale: subtitleLocale,
              name: matches[4],
            };
          }
        }
      } else {
        file.error = `Invalid file name`;
        errors.push(file.error);
      }
    }
    validatedFiles[uuid] = file;
  }

  const videoKeys = Object.keys(episodes)
    .map((key) => parseInt(key, 10))
    .sort((a, b) => a - b);
  const keys = Array.from({ length: Math.max(...videoKeys) }, (_, i) => i + 1);
  if (videoKeys.includes(0)) {
    keys.unshift(0);
  }

  keys.forEach((key) => {
    if (key === 0) {
      episodes[key].trailer = true;
    }
    if (!videoKeys.includes(key)) {
      const episodeExists = existingEpisodes.find(
        (episode) => episode.ordinal === key
      );
      if (!episodeExists) {
        episodes[key] = { file: null, name: `Placeholder for episode ${key}` };
      }
    }
  });

  const subtitleEntries = Object.entries(subtitleData);

  for (let i = 0; i < subtitleEntries.length; i += 1) {
    const [key, localeSubtitles] = subtitleEntries[i];

    const localeEntries = Object.entries(localeSubtitles);

    if (isSubtitles && key > numEpisodes) {
      for (let j = 0; j < localeEntries.length; j += 1) {
        const [, subtitleEntry] = localeEntries[j];
        subtitleEntry.file.error = `Subtitle for episode ${key} does not match with any episode`;
        errors.push(subtitleEntry.file.error);
      }
    } else {
      if (isSubtitles) {
        episodes[key] = { file: null, subtitles: {} };
      }

      if (!isSubtitles && !(key in episodes)) {
        const episodeExists = existingEpisodes.find(
          (episode) => episode.ordinal === parseInt(key, 10)
        );
        if (episodeExists) {
          episodes[key] = { file: null, subtitles: {} };
        }
      }

      if (key in episodes) {
        if (!episodes[key].subtitles) {
          episodes[key].subtitles = {};
        }

        for (let j = 0; j < localeEntries.length; j += 1) {
          const [localeKey, value] = localeEntries[j];
          if (!isSupportedLocale(value.locale)) {
            if (value.locale) {
              value.file.error = `Subtitle for episode ${key} has an unsupported language, ${value.locale}`;
              errors.push(value.file.error);
            } else {
              value.file.error = `Subtitle for episode ${key} has no language specified`;
              errors.push(value.file.error);
            }
            episodes[key].subtitles[localeKey] = value.file;
            episodes[key].subtitles[localeKey].hasError = true;
          } else {
            episodes[key].subtitles[value.locale] = value.file;
            if (!episodes[key].file) {
              episodes[key].name = value.name;
            }
          }
        }
      } else if (key > numEpisodes) {
        for (let j = 0; j < localeEntries.length; j += 1) {
          const [, value] = localeEntries[j];
          value.file.error = `Subtitle for episode ${key} does not match with a video file`;
          errors.push(value.file.error);
        }
      }
    }
  }

  return { episodes, validatedFiles, ignored, errors };
};

export const parseCourseFiles = (course, files, isSubtitles) => {
  const existingEpisodes = course ? course.episodes : [];
  const regex = /^([0-9]+)_(?:(.+)\.mp4|([a-z]{2}_)?(.+)\.(srt|vtt))$/i;

  const videoData = {};
  const subtitleData = {};
  const ignored = [];

  for (let i = 0; i < files.length; i += 1) {
    const file = files[i];
    const matches = file.match(regex);

    if (matches) {
      const prefix = parseInt(matches[1], 10);

      if (Number.isNaN(prefix) || prefix < 0) {
        ignored.push(file);
      } else if (matches[2]) {
        if (!videoData[prefix]) {
          if (!isSubtitles) {
            videoData[prefix] = { file, name: matches[2] };
          } else {
            ignored.push(file);
          }
        } else {
          throw new Error(`Repeated episode number: ${prefix}`);
        }
      } else {
        const subtitleLocale = matches[3]?.slice(0, 2).toLowerCase();
        if (!subtitleData[prefix]) {
          subtitleData[prefix] = {};
        }
        if (subtitleData[prefix][subtitleLocale || 'unknown']) {
          throw new Error(
            `Repeated subtitle for episode ${prefix} with locale ${subtitleLocale || 'unknown'}`
          );
        } else {
          subtitleData[prefix][subtitleLocale || 'unknown'] = {
            file,
            locale: subtitleLocale,
            name: matches[4],
          };
        }
      }
    } else {
      ignored.push(file);
    }
  }

  const videoKeys = Object.keys(videoData)
    .map((key) => parseInt(key, 10))
    .sort((a, b) => a - b);
  const keys = Array.from({ length: Math.max(...videoKeys) }, (_, i) => i + 1);
  if (videoKeys.includes(0)) {
    keys.unshift(0);
  }

  keys.forEach((key) => {
    if (key === 0) {
      videoData[key].trailer = true;
    }
    if (!videoKeys.includes(key)) {
      const episodeExists = existingEpisodes.find(
        (episode) => episode.ordinal === key
      );
      if (!episodeExists) {
        videoData[key] = {
          file: null,
          name: `Placeholder for episode ${key}`,
        };
      }
    }
  });

  const subtitleEntries = Object.entries(subtitleData);

  for (let i = 0; i < subtitleEntries.length; i += 1) {
    const [key, localeSubtitles] = subtitleEntries[i];

    if (isSubtitles) {
      videoData[key] = { file: null, subtitles: {} };
    }

    if (!isSubtitles && !(key in videoData)) {
      const episodeExists = existingEpisodes.find(
        (episode) => episode.ordinal === parseInt(key, 10)
      );
      if (episodeExists) {
        videoData[key] = { file: null, subtitles: {} };
      }
    }

    const localeEntries = Object.entries(localeSubtitles);

    if (key in videoData) {
      if (!videoData[key].subtitles) {
        videoData[key].subtitles = {};
      }

      for (let j = 0; j < localeEntries.length; j += 1) {
        const [localeKey, value] = localeEntries[j];
        if (!isSupportedLocale(value.locale)) {
          videoData[key].subtitles[localeKey] = {
            file: value.file,
            hasError: true,
            errorType: value.locale ? 'unsupported' : 'missing',
          };
        } else {
          videoData[key].subtitles[value.locale] = value.file;
          if (!videoData[key].file) {
            videoData[key].name = value.name;
          }
        }
      }
    } else {
      for (let j = 0; j < localeEntries.length; j += 1) {
        const [, value] = localeEntries[j];
        ignored.push(value.file);
      }
    }
  }

  if (videoData[0]) {
    videoData[0].trailer = true;
  }

  return { episodes: videoData, ignored };
};

export { SUPPORTED_LOCALES };
